import { create } from 'zustand';
import useSceneStore from './sceneStore';

// ============================================
// 测量全局状态（独立 store，场景渲染/工具栏/面板双向同步）
// 用例：两点距离(斜距+水平+高差) / 三点角度 / 多点闭合面积 / 物体包围盒尺寸
// ============================================

export type MeasureMode = 'none' | 'distance' | 'angle' | 'area' | 'boxSize';

export interface MeasurePoint {
  x: number;
  y: number;
  z: number;
}

export interface MeasureEntity {
  id: string;
  mode: Exclude<MeasureMode, 'none'>;
  points: MeasurePoint[];
  result: Record<string, number>;
  visible: boolean;
}

interface MeasureState {
  currentMode: MeasureMode;
  /** 网格/顶点吸附开关 */
  snapEnable: boolean;
  /** 单位 m / mm */
  unit: 'm' | 'mm';
  /** 正在绘制的临时点 */
  tempPoints: MeasurePoint[];
  /** 全部测量记录 */
  list: MeasureEntity[];

  setMode: (mode: MeasureMode) => void;
  toggleSnap: () => void;
  setUnit: (u: 'm' | 'mm') => void;
  addTempPoint: (p: MeasurePoint) => void;
  undoTempPoint: () => void;
  clearTempPoints: () => void;
  saveMeasure: (entity: Omit<MeasureEntity, 'id' | 'visible'>) => void;
  removeMeasure: (id: string) => void;
  clearAllMeasure: () => void;
  toggleMeasureVisible: (id: string) => void;
  setList: (list: MeasureEntity[]) => void;
}

// 同步到 sceneStore.measurements（场景保存/加载兼容）
function syncToScene(list: MeasureEntity[]) {
  useSceneStore.getState().setMeasurements(
    list.map((m) => {
      const unitMap: Record<string, string> = { distance: 'm', angle: '°', area: 'm²', boxSize: 'm' };
      const value = m.mode === 'distance' ? m.result.slant ?? 0
        : m.mode === 'angle' ? m.result.angle ?? 0
        : m.mode === 'area' ? m.result.area ?? 0
        : m.result.width ?? 0;
      return {
        id: m.id,
        type: m.mode as 'distance' | 'angle' | 'area',
        points: m.points.map((p, i) => ({ position: p, id: `${m.id}-p${i}` })),
        value,
        unit: unitMap[m.mode] ?? 'm',
        mode: m.mode,
        result: m.result,
        visible: m.visible,
      } as never;
    }),
  );
}

export const useMeasureStore = create<MeasureState>((set, get) => ({
  currentMode: 'none',
  snapEnable: false,
  unit: 'm',
  tempPoints: [],
  list: [],

  setMode: (mode) => set({ currentMode: mode, tempPoints: [] }),

  toggleSnap: () => set((s) => ({ snapEnable: !s.snapEnable })),

  setUnit: (unit) => set({ unit }),

  addTempPoint: (point) => set((s) => ({ tempPoints: [...s.tempPoints, point] })),

  undoTempPoint: () => set((s) => ({ tempPoints: s.tempPoints.slice(0, -1) })),

  clearTempPoints: () => set({ tempPoints: [] }),

  saveMeasure: (entity) => {
    const id = `measure-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    const list = [...get().list, { ...entity, id, visible: true }];
    set({ list, tempPoints: [] });
    syncToScene(list);
  },

  removeMeasure: (id) => {
    const list = get().list.filter((i) => i.id !== id);
    set({ list });
    syncToScene(list);
  },

  clearAllMeasure: () => {
    set({ list: [], tempPoints: [] });
    syncToScene([]);
  },

  toggleMeasureVisible: (id) => {
    const list = get().list.map((item) =>
      item.id === id ? { ...item, visible: !item.visible } : item
    );
    set({ list });
    syncToScene(list);
  },

  setList: (list) => set({ list }),
}));
